import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Title, 
  Paper, 
  Stack, 
  Group, 
  Button, 
  TextInput, 
  NumberInput,
  Grid,
  Alert,
  Loader,
  Center,
  Text,
  Modal,
  Badge,
  Tabs
} from '@mantine/core';
import { 
  IconMapPin, 
  IconReportAnalytics, 
  IconAlertCircle,
  IconFileText,
  IconHistory
} from '@tabler/icons-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ToastProperties } from '../utils/constants/toastProperties';
import { isAuthenticated } from '../utils/tokenValidator';
import { logout } from '../Redux/user/slice';
import EudrMapSelector from '../components/EudrMapSelector';
import EudrReportCard from '../components/EudrReportCard';
import { LoginModal } from '../components/LoginModal';
import { 
  generateReport,
  fetchReports, 
  deleteReport,
  clearError,
  clearSuccess 
} from '../Redux/eudr/slice';

/**
 * EudrReportsPage
 * 
 * Main page for EUDR compliance reports
 * - Map selection for GPS points or polygons
 * - Report generation
 * - Report history and management
 */
const EudrReportsPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Redux state
  const userinfo = useSelector((state) => state.user.userinfo);
  const { reports, generating, loading, error, success } = useSelector((state) => state.eudr);
  
  // Local state
  const [activeTab, setActiveTab] = useState('generate');
  const [fieldName, setFieldName] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [zoom, setZoom] = useState(17);
  const [viewingReport, setViewingReport] = useState(null);

  // Check token validity on component mount
  useEffect(() => {
    if (!isAuthenticated()) {
      console.warn('Token expired or invalid - logging out user');
      
      // Show toast notification
      toast.error(<FormattedMessage id="toast.sessionexpired" />, ToastProperties);
      
      // Preserve tour completion - only clear user-specific data
      const tourCompleted = localStorage.getItem('vertoWorkspaceTourCompleted');
      localStorage.clear();
      if (tourCompleted) {
        localStorage.setItem('vertoWorkspaceTourCompleted', tourCompleted);
      }
      
      // Dispatch logout
      dispatch(logout());
      
      // Redirect to home after a short delay
      setTimeout(() => {
        navigate('/');
      }, 1000);
    }
  }, [dispatch, navigate]);

  // Fetch reports from backend when component mounts or tab changes to history
  useEffect(() => {
    const accountId = userinfo?.accountId || 'TEST_ACCOUNT';
    if (activeTab === 'history' && accountId) {
      dispatch(fetchReports(accountId));
    }
  }, [activeTab, userinfo, dispatch]);

  // Handle success after report generation - switch to history tab and refresh
  useEffect(() => {
    if (success && success.includes('generated')) {
      toast.success(success, ToastProperties);
      dispatch(clearSuccess());
      
      setActiveTab('history');
      setFieldName('');
      setSelectedLocation(null);
      
      // Refresh reports list from backend
      const accountId = userinfo?.accountId || 'TEST_ACCOUNT';
      dispatch(fetchReports(accountId));
    }
  }, [success, dispatch, userinfo]);

  // Handle success/error toasts
  useEffect(() => {
    if (success && !success.includes('generated')) {
      toast.success(success, ToastProperties);
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error, ToastProperties);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const handleGenerateReport = () => {
    if (!fieldName.trim()) {
      toast.error(
        intl.formatMessage({ 
          id: 'eudr.error.fieldName', 
          defaultMessage: 'Please enter a field name' 
        }), 
        ToastProperties
      );
      return;
    }

    if (!selectedLocation) {
      toast.error(
        intl.formatMessage({ 
          id: 'eudr.error.location', 
          defaultMessage: 'Please select a location on the map' 
        }), 
        ToastProperties
      );
      return;
    }

    const params = {
      fieldName,
      accountId: userinfo?.accountId || 'TEST_ACCOUNT',
      zoom,
      tileRadius: 2
    };

    if (selectedLocation.type === 'point') {
      params.latitude = selectedLocation.latitude;
      params.longitude = selectedLocation.longitude;
    } else {
      params.polygon = selectedLocation.polygon;
      params.latitude = selectedLocation.polygon[0].lat;
      params.longitude = selectedLocation.polygon[0].lon;
    }

    dispatch(generateReport(params));
  };

  const handleDownloadReport = (report) => {
    // Prioritize pdfUrl over documentUrl (documentUrl might be .docx)
    const url = report.pdfUrl || report.documentUrl;
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.error('PDF URL not found', ToastProperties);
    }
  };

  const handleViewReport = (report) => {
    setViewingReport(report);
  };

  const handleDeleteReport = (report) => {
    if (window.confirm(
      intl.formatMessage({ 
        id: 'eudr.confirm.delete', 
        defaultMessage: 'Are you sure you want to delete this report?' 
      })
    )) {
      const accountId = userinfo?.accountId || 'TEST_ACCOUNT';
      const isTestAccount = accountId === 'TEST_ACCOUNT';
      
      if (!isTestAccount && report.reportId) {
        // Delete from backend/blob for non-test accounts
        dispatch(deleteReport({ reportId: report.reportId, accountId }));
      } else {
        // For test accounts, just show success message (files kept in blob)
        toast.success(
          intl.formatMessage({ 
            id: 'eudr.success.deleted', 
            defaultMessage: 'Report removed from view (file preserved in storage)' 
          }), 
          ToastProperties
        );
      }
    }
  };

  return (
    <Container size="xl" py="xl" style={{ pointerEvents: 'auto' }}>
      <Stack spacing="xl">
        {/* Header */}
        <Paper p="xl" withBorder>
          <Group position="apart">
            <div>
              <Title order={1}>
                <FormattedMessage 
                  id="eudr.title" 
                  defaultMessage="EUDR Field Reports" 
                />
              </Title>
              <Text color="dimmed" mt="xs">
                <FormattedMessage 
                  id="eudr.subtitle" 
                  defaultMessage="Generate environmental traceability reports with satellite imagery for EU Deforestation Regulation compliance" 
                />
              </Text>
            </div>
            <IconReportAnalytics size={48} color="#228be6" />
          </Group>
        </Paper>

        {/* Tabs */}
        <Tabs value={activeTab} onTabChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="generate" icon={<IconMapPin size={14} />}>
              <FormattedMessage id="eudr.tab.generate" defaultMessage="Generate Report" />
            </Tabs.Tab>
            <Tabs.Tab value="history" icon={<IconHistory size={14} />}>
              <FormattedMessage id="eudr.tab.history" defaultMessage="Report History" />
              {reports.length > 0 && (
                <Badge ml="xs" size="sm" variant="filled">
                  {reports.length}
                </Badge>
              )}
            </Tabs.Tab>
          </Tabs.List>

          {/* Generate Tab */}
          <Tabs.Panel value="generate" pt="xl">
            <Grid gutter="xl">
              <Grid.Col span={12} md={6}>
                <Paper p="md" withBorder>
                  <Stack spacing="md">
                    <Title order={3}>
                      <FormattedMessage id="eudr.form.title" defaultMessage="Report Configuration" />
                    </Title>

                    <TextInput
                      label={<FormattedMessage id="eudr.form.fieldName" defaultMessage="Field Name" />}
                      placeholder={intl.formatMessage({ 
                        id: 'eudr.form.fieldName.placeholder', 
                        defaultMessage: 'Enter field or farm name' 
                      })}
                      value={fieldName}
                      onChange={(e) => setFieldName(e.target.value)}
                      required
                    />

                    <NumberInput
                      label={<FormattedMessage id="eudr.form.zoom" defaultMessage="Zoom Level" />}
                      description={<FormattedMessage id="eudr.form.zoom.description" defaultMessage="Higher zoom = more detail (recommended: 17)" />}
                      value={zoom}
                      onChange={setZoom}
                      min={15}
                      max={19}
                    />

                    {selectedLocation && (
                      <Alert color="green" icon={<IconMapPin size={16} />}>
                        <Text size="sm" weight={500}>
                          {selectedLocation.type === 'point' ? (
                            <FormattedMessage 
                              id="eudr.form.location.point" 
                              defaultMessage="Single GPS point selected"
                            />
                          ) : (
                            <FormattedMessage 
                              id="eudr.form.location.polygon" 
                              defaultMessage="Polygon with {count} vertices selected"
                              values={{ count: selectedLocation.polygon?.length }}
                            />
                          )}
                        </Text>
                      </Alert>
                    )}

                    <Button
                      fullWidth
                      size="lg"
                      onClick={handleGenerateReport}
                      loading={generating}
                      disabled={!fieldName || !selectedLocation}
                      leftIcon={<IconFileText size={20} />}
                    >
                      <FormattedMessage id="eudr.button.generate" defaultMessage="Generate Report" />
                    </Button>

                    {generating && (
                      <Alert icon={<IconAlertCircle size={16} />} color="blue">
                        <Text size="sm">
                          <FormattedMessage 
                            id="eudr.generating.message" 
                            defaultMessage="Generating report... This may take a few minutes as we collect satellite imagery." 
                          />
                        </Text>
                      </Alert>
                    )}
                  </Stack>
                </Paper>
              </Grid.Col>

              <Grid.Col span={12} md={6}>
                <EudrMapSelector 
                  onLocationSelect={handleLocationSelect}
                  disabled={generating}
                />
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          {/* History Tab */}
          <Tabs.Panel value="history" pt="xl">
            {loading ? (
              <Paper p="xl" withBorder>
                <Center>
                  <Stack align="center" spacing="md">
                    <Loader size="lg" />
                    <Text size="lg" color="dimmed">
                      <FormattedMessage 
                        id="eudr.history.loading" 
                        defaultMessage="Loading reports..." 
                      />
                    </Text>
                  </Stack>
                </Center>
              </Paper>
            ) : reports.length === 0 ? (
              <Paper p="xl" withBorder>
                <Center>
                  <Stack align="center" spacing="md">
                    <IconReportAnalytics size={64} color="#adb5bd" />
                    <Text size="lg" color="dimmed">
                      <FormattedMessage 
                        id="eudr.history.empty" 
                        defaultMessage="No reports generated yet" 
                      />
                    </Text>
                    <Button onClick={() => setActiveTab('generate')}>
                      <FormattedMessage 
                        id="eudr.history.empty.button" 
                        defaultMessage="Generate Your First Report" 
                      />
                    </Button>
                  </Stack>
                </Center>
              </Paper>
            ) : (
              <Grid gutter="md">
                {reports.map((report) => (
                  <Grid.Col key={report.reportId} span={12} md={6} lg={4}>
                    <EudrReportCard
                      report={report}
                      onView={handleViewReport}
                      onDownload={handleDownloadReport}
                      onDelete={handleDeleteReport}
                    />
                  </Grid.Col>
                ))}
              </Grid>
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>

      {/* Report Details Modal */}
      <Modal
        opened={!!viewingReport}
        onClose={() => setViewingReport(null)}
        title={viewingReport?.fieldName}
        size="xl"
      >
        {viewingReport && (
          <Stack spacing="md">
            <Group>
              <Text weight={500}>
                <FormattedMessage id="eudr.modal.reportId" defaultMessage="Report ID:" />
              </Text>
              <Text>{viewingReport.reportId}</Text>
            </Group>
            
            <Group>
              <Text weight={500}>
                <FormattedMessage id="eudr.modal.location" defaultMessage="Location:" />
              </Text>
              <Text>
                {viewingReport.location?.latitude?.toFixed(6)}, {viewingReport.location?.longitude?.toFixed(6)}
              </Text>
            </Group>

            <div>
              <Text weight={500} mb="xs">
                <FormattedMessage id="eudr.modal.dates" defaultMessage="Dates Included:" />
              </Text>
              <Group spacing="xs">
                {viewingReport.datesIncluded?.map((date, index) => (
                  <Badge key={index} variant="outline">
                    {date}
                  </Badge>
                ))}
              </Group>
            </div>

            <Button 
              fullWidth
              onClick={() => handleDownloadReport(viewingReport)}
              leftIcon={<IconFileText size={16} />}
            >
              <FormattedMessage id="eudr.modal.download" defaultMessage="Download Report" />
            </Button>
          </Stack>
        )}
      </Modal>
      <LoginModal />
    </Container>
  );
};

export default EudrReportsPage;
